import { getErrorCode, getErrorMessage } from '@/lib/error-utils';

export interface ScreenplayUploadState {
  isSupported: boolean;
  currentCount: number;
  canUpload: boolean;
  canCreate: boolean;
  shouldShowUploadEntry: boolean;
}

interface GetScreenplayUploadStateParams {
  isSupported: boolean;
  hasPermission?: boolean;
  hasCreatePermission?: boolean;
  currentCount: number;
}

export function getScreenplayUploadState({
  isSupported,
  hasPermission = true,
  hasCreatePermission,
  currentCount,
}: GetScreenplayUploadStateParams): ScreenplayUploadState {
  const normalizedCurrentCount = Math.max(0, currentCount);
  const canCreate = hasCreatePermission ?? hasPermission;

  if (!isSupported) {
    return {
      isSupported: false,
      currentCount: normalizedCurrentCount,
      canUpload: false,
      canCreate: false,
      shouldShowUploadEntry: false,
    };
  }

  return {
    isSupported: true,
    currentCount: normalizedCurrentCount,
    canUpload: hasPermission,
    canCreate,
    shouldShowUploadEntry: canCreate,
  };
}

const SAFE_SCREENPLAY_ERROR_MARKERS = [
  '剧本准备失败，请重试',
  '文件解析后内容为空',
  '文件内容为空，请上传有效文件',
  '不支持的文件格式，请上传',
  '该 PDF 为图片型文件',
  'PDF 文件未提取到任何文本内容',
  '文档结构过于复杂或已损坏',
  '文件未包含有效文本正文',
  '无法读取文档正文',
  '文本文件编码不支持',
  '剧本超过 ',
  '文件内容与声明格式不符',
  '文件格式不匹配或内容已损坏',
] as const;

const SCREENPLAY_PARSE_FAILURE = '剧本解析失败，请重试；若仍失败，请重新上传文件或联系支持人员。';

function screenplayErrorHasInternalDetail(message: string): boolean {
  if (/https?:\/\/|file:\/\/|\\/i.test(message)) return true;
  return message.split('Word/WPS').join('').includes('/');
}

export function nextParseStatusFailureCount(
  previous: { lastErrorAt: number; count: number },
  state: { errorUpdatedAt: number; dataUpdatedAt: number; hasError: boolean }
): { lastErrorAt: number; count: number } {
  if (!state.hasError || state.dataUpdatedAt > state.errorUpdatedAt) {
    return { lastErrorAt: 0, count: 0 };
  }
  if (state.errorUpdatedAt !== previous.lastErrorAt) {
    return { lastErrorAt: state.errorUpdatedAt, count: previous.count + 1 };
  }
  return previous;
}

/** 已知的安全中文原样返回；带地址或路径的诊断信息换成受控句。 */
export function toPublicScreenplayMessage(message: string): string {
  const text = message.trim();
  if (/timeout|超时/i.test(text)) return '剧本处理超时，请稍后重试。';
  if (
    text &&
    !screenplayErrorHasInternalDetail(text) &&
    SAFE_SCREENPLAY_ERROR_MARKERS.some((marker) => text.includes(marker))
  ) {
    return text;
  }
  return SCREENPLAY_PARSE_FAILURE;
}

export function resolveScreenplaySourceStatus(input: {
  nonRetryableParseError: boolean;
  gaveUp: boolean;
  hasParsingError: boolean;
  awaitingExternalStatus: boolean;
  parsing: boolean;
  markdownLoading: boolean;
  detailPendingWithoutContent: boolean;
  parseRequestFailed: boolean;
  hasContent: boolean;
  hasOtherError: boolean;
}): 'loading' | 'error' | 'ready' | 'empty' {
  if (input.nonRetryableParseError || input.gaveUp) return 'error';
  if (input.hasParsingError) return 'error';
  if (
    input.awaitingExternalStatus ||
    input.parsing ||
    input.markdownLoading ||
    input.detailPendingWithoutContent
  ) {
    return 'loading';
  }
  if (input.parseRequestFailed && input.hasContent) return 'ready';
  if (input.hasOtherError || input.parseRequestFailed) return 'error';
  if (input.hasContent) return 'ready';
  return 'empty';
}

/** 上传失败的用户说明；底层异常仍由调用方记录，避免直接展示存储或解析器细节。 */
export function getScreenplayUploadError(
  error: unknown,
  fileName: string
): { title: string; description: string } {
  const code = getErrorCode(error);
  const rawMessage = getErrorMessage(error, '');
  const message = typeof rawMessage === 'string' ? rawMessage : '';
  let title = '未能导入剧本';
  let reason = '暂时无法完成导入，请稍后重试；若仍失败，请联系支持人员。';

  if (code === 408 || /timeout|超时/i.test(message)) {
    title = '文档处理超时';
    reason = '请稍后刷新查看导入结果；若仍未导入，再重新上传。';
  } else if (/network|网络|failed to fetch/i.test(message)) {
    title = '上传连接中断';
    reason = '请检查网络连接，稍后刷新查看导入结果；若未导入，再重新上传。';
  } else if (code && code >= 400 && code < 500) {
    const safeMessage = toPublicScreenplayMessage(message);
    const hasActionableMessage = safeMessage !== SCREENPLAY_PARSE_FAILURE;
    if (hasActionableMessage) {
      if (
        safeMessage.includes('文件内容与声明格式不符') ||
        safeMessage.includes('文件格式不匹配') ||
        safeMessage.includes('另存为') ||
        safeMessage.includes('不支持的文件格式') ||
        safeMessage.includes('图片型')
      ) {
        title = '无法读取文档';
      }
      reason = safeMessage;
    } else if (code === 413) {
      reason = '文件过大，请缩小文件后重新上传。';
    } else if (code === 401 || code === 403) {
      reason = '登录状态已失效或没有上传权限，请重新登录或联系项目管理员。';
    }
  }

  return { title, description: `“${fileName}”：${reason}` };
}

/** 异步任务或历史缓存中的错误只能映射成受控提示，不能直接显示异常原文。 */
export function getScreenplayParseError(error: unknown): string {
  const rawMessage = typeof error === 'string' ? error : getErrorMessage(error, '');
  const message = typeof rawMessage === 'string' ? rawMessage : '';
  return toPublicScreenplayMessage(message);
}
